import type {ReactElement} from 'react';
import React from 'react';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {TextSlideFontButton} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-font-button';
import {TextSlideStylingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-styling-options';
import {TextSlideSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-size-option';
import {TextSlideColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-color-option';
import {TextSlideOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-outline-option';
import {TextSlideAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-alignment-option';
import {TextSlideVerticalAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-vertical-alignment-option';
import {TextSlideLineHeightOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-line-height-option';
import {TextSlideLetterSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-letter-spacing-option';
import {ItemInteractivitySection} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-section';
import {TextSlideListOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-list-option';
import {TextSlideBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-background-option';
import {SlideshowItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-shadow-option';
import {SlideShowPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-slideshow-options/poster-editing-slideshow-options.types';
import {SlideshowSlideSection} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-slide-section';
import {ImageSlideMediaControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-slide-media-controls';
import {SlideshowItemEffectsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-effects-option';
import {SlideshowItemGlowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-glow-option';
import {SlideshowItemBlackAndWhiteOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-black-and-white-option';
import {SlideshowItemSepiaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-sepia-option';
import {SlideshowItemInvertOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-invert-option';
import {SlideshowItemBrightnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-brightness-option';
import {SlideshowItemContrastOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-contrast-option';
import {SlideshowItemVibranceOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-vibrance-option';
import {SlideshowItemSaturationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-saturation-option';
import {SlideshowItemTintOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-tint-option';
import {SlideshowItemMultiplyOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-multiply-option';
import {SlideshowItemGammaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-gamma-option';
import {SlideshowItemRemoveColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-item-remove-color-option';
import {VideoSlideMediaControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-slide-media-controls';
import {SlideshowApplyPropertiesInfobox} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-apply-properties-infobox';
import {MediaSlideAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/media-slide-alignment-option';
import {LanguageNotSupportedByTextslideFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/language-not-supported-by-textslide-font-option';
import {ImageSlideRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-slide-roundness-option/image-slide-roundness-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {AnimationPanelType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {VideoSlideItemVolumeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-slide-item-volume-option/video-slide-item-volume-option';

interface BaseSlideshowEditOptionsProps {
  onOpenPanel(selectedPanel: SlideShowPanels): void;
}

export function BaseSlideshowEditOptions({...props}: BaseSlideshowEditOptionsProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();

  if (!activeItem) {
    throw new Error('Unable to show slideshow options since active item does not exist');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected a slideshow item but got: ${activeItem?.gitype}`);
  }

  const activeSlideItem = activeSlideUID ? activeItem.getSelectedSlide() : undefined;

  if (!activeSlideItem) {
    throw new Error(`No Active Slide Item`);
  }

  const getSlideshowOptions = (): ReactElement<any> => {
    switch (activeItem.getSelectedSlide().gitype) {
      case ITEM_TYPE.TEXTSLIDE:
        return getTextOptions();
      case ITEM_TYPE.IMAGESLIDE:
        return getImageOptions();
      case ITEM_TYPE.VIDEOSLIDE:
        return getVideoOptions();
      default:
        throw new Error('unhandled slide type for slideshow');
    }
  };

  const getSlideshowSlidesSection = (): ReactElement<any> => {
    return (
      <SlideshowSlideSection
        onClickTransitionColorFill={(): void => {
          props.onOpenPanel(SlideShowPanels.BLOCK_TRANSITION);
        }}
      />
    );
  };

  const getTextOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(type: AnimationPanelType): void => {
            props.onOpenPanel(type === AnimationPanelType.INTRO_ANIMATION_STYLES ? SlideShowPanels.INTRO_ANIMATION : SlideShowPanels.OUTRO_ANIMATION);
          }}
        />
        {getSlideshowSlidesSection()}
        <ContextualMenuHeadingOption id="text-slide-styles-heading-option" text={window.i18next.t('pmwjs_styles')} />
        <SlideshowApplyPropertiesInfobox />
        <TextSlideFontButton
          onClickFontButton={(): void => {
            props.onOpenPanel(SlideShowPanels.FONT);
          }}
        />
        <LanguageNotSupportedByTextslideFontOption />
        <TextSlideStylingOptions />
        <TextSlideSizeOption />
        <TextSlideColorOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.TEXT_FILL);
          }}
        />
        <TextSlideOutlineOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.OUTLINE);
          }}
        />
        <TextSlideAlignmentOption />
        <TextSlideVerticalAlignmentOption />
        <TextSlideLineHeightOption />
        <TextSlideLetterSpacingOption />
        <TextSlideListOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.LIST_COLOR);
          }}
        />
        <TextSlideBackgroundOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.BACKGROUND_COLOR);
          }}
        />
        <SlideshowItemShadowOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.SHADOW);
          }}
        />
        <ItemInteractivitySection />
      </>
    );
  };

  const getOptionsForMediaSlides = (isImageSlide = false): ReactElement<any> => {
    return (
      <>
        <ContextualMenuHeadingOption id="media-slide-effects-heading-option" text={window.i18next.t('pmwjs_effects')} />
        <SlideshowApplyPropertiesInfobox />
        <MediaSlideAlignmentOption />
        <SlideshowItemEffectsOption />
        <SlideshowItemShadowOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.SHADOW);
          }}
        />
        <SlideshowItemGlowOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.GLOW);
          }}
        />
        {isImageSlide ? <ImageSlideRoundnessOption /> : null}
        <SlideshowItemBlackAndWhiteOption />
        <SlideshowItemSepiaOption />
        <SlideshowItemInvertOption />
        {isImageSlide ? (
          <SlideshowItemRemoveColorOption
            onClickColorFill={(): void => {
              props.onOpenPanel(SlideShowPanels.REMOVE_COLOR);
            }}
          />
        ) : null}
        <ContextualMenuHeadingOption id="media-slide-effects-heading-option" text={window.i18next.t('pmwjs_adjustments')} />
        <SlideshowItemBrightnessOption />
        <SlideshowItemContrastOption />
        <SlideshowItemVibranceOption />
        <SlideshowItemSaturationOption />
        <SlideshowItemTintOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.TINT);
          }}
        />
        <SlideshowItemMultiplyOption
          onClickColorFill={(): void => {
            props.onOpenPanel(SlideShowPanels.MULTIPLY);
          }}
        />
        <SlideshowItemGammaOption />
      </>
    );
  };

  const getImageOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(type: AnimationPanelType): void => {
            props.onOpenPanel(type === AnimationPanelType.INTRO_ANIMATION_STYLES ? SlideShowPanels.INTRO_ANIMATION : SlideShowPanels.OUTRO_ANIMATION);
          }}
        />
        {getSlideshowSlidesSection()}
        <ContextualMenuHeadingOption id="image-actions-heading-option" text={window.i18next.t('pmwjs_actions')} />
        <ImageSlideMediaControls />
        {getOptionsForMediaSlides(true)}

        <ItemInteractivitySection />
      </>
    );
  };

  const getVideoOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(type: AnimationPanelType): void => {
            props.onOpenPanel(type === AnimationPanelType.INTRO_ANIMATION_STYLES ? SlideShowPanels.INTRO_ANIMATION : SlideShowPanels.OUTRO_ANIMATION);
          }}
        />
        {getSlideshowSlidesSection()}
        <ContextualMenuHeadingOption id="video-actions-heading-option" text={window.i18next.t('pmwjs_actions')} />
        <VideoSlideMediaControls />
        <VideoSlideItemVolumeOption />
        {getOptionsForMediaSlides()}
        <ItemInteractivitySection />
      </>
    );
  };

  return getSlideshowOptions();
}
